export function formatElapsed (ms) {
	const total = Math.max(0, Math.round(Number(ms) / 1000) || 0);
	const minutes = Math.floor(total / 60);
	const seconds = total % 60;
	if (minutes <= 0) return `${seconds}s`;
	return `${minutes}m ${seconds}s`;
}

export function formatNarrativeProgress (status) {
	if (!status || (status.status !== 'pending' && status.status !== 'running')) return '';
	const clock = formatElapsed(status.elapsedMs || 0);
	if (status.status === 'pending') return `Queued… ${clock}`;
	const progress = status.progress || {};
	if (progress.chapter && progress.chapterCount) {
		const tokens = progress.phase === 'generating' && progress.completionTokens != null
			? `${progress.usageEstimated ? '~' : ''}${progress.completionTokens} tokens, `
			: '';
		const label = progress.retry ? 'Retrying chapter' : 'Chapter';
		return `${label} ${progress.chapter} of ${progress.chapterCount}… ${tokens}${clock}`;
	}
	if (progress.phase === 'preparing') return `Preparing… ${clock}`;
	if (progress.phase === 'loading') return `Loading model… ${clock}`;
	if (progress.phase === 'generating') {
		const tokens = progress.completionTokens != null
			? `${progress.usageEstimated ? '~' : ''}${progress.completionTokens} tokens, `
			: '';
		return `Writing… ${tokens}${clock}`;
	}
	return `Waiting for the first token… ${clock}`;
}

export function localNarrativeStatus (health, style = 'panel') {
	if (!health) return 'Checking…';
	if (!health.localConfigured) {
		return style === 'admin'
			? 'Not configured — add LLM_BASE_URL and LLM_MODEL to .env'
			: 'Not configured';
	}
	if (!health.reachable) {
		return `Unreachable${health.error ? ` — ${health.error}` : ''}`;
	}
	const narrative = String(health.narrativeModel || '').trim();
	const used = narrative || health.resolvedModel || health.model || 'model set';
	const prefix = style === 'admin' ? 'Reachable' : 'Ready';
	if (health.loadState === 'loaded') return `${used} (loaded)`;
	if (health.loadState === 'not-loaded') return `${used} (not loaded, will load on demand)`;
	if (health.loadState === 'not-downloaded') return `${used} (not downloaded)`;
	if (narrative && health.narrativeModelLoaded === false) {
		if (health.narrativeModelsKnown === false) {
			return health.error || `${narrative} could not be confirmed in LM Studio; /v1/models returned no models.`;
		}
		const loaded = Array.isArray(health.models) && health.models.length
			? health.models.join(', ')
			: 'none';
		return `${narrative} is not loaded in LM Studio; loaded: ${loaded}`;
	}
	return `${prefix} (${used})`;
}

export function preloadProxyFailure (value) {
	if (value == null) return false;
	const status = typeof value === 'object' ? Number(value.status || value.httpStatus || 0) : 0;
	if (status === 502 || status === 504) return true;
	const text = typeof value === 'string'
		? value
		: String((value && (value.error || value.message)) || '');
	const blob = text.toLowerCase();
	if (blob.includes('gateway time-out') || blob.includes('gateway timeout')) return true;
	if (blob.includes('invalid json')) return true;
	if (blob.includes('<html') && (blob.includes('504') || blob.includes('502') || blob.includes('gateway') || blob.includes('time-out') || blob.includes('timeout'))) {
		return true;
	}
	if (/\bhttp (502|504)\b/.test(blob)) return true;
	return false;
}

function preloadElapsedSeconds (preload, nowMs) {
	const now = Number.isFinite(Number(nowMs)) ? Number(nowMs) : Date.now();
	let reported = null;
	if (preload.elapsedSeconds != null && preload.elapsedSeconds !== '') {
		const n = Number(preload.elapsedSeconds);
		if (Number.isFinite(n)) reported = Math.max(0, Math.round(n));
	}
	let fromStart = null;
	if (preload.startedAt) {
		const start = Number(preload.startedAt);
		if (Number.isFinite(start) && start > 0) {
			const startMs = start > 1e12 ? start : start * 1000;
			fromStart = Math.max(0, Math.round((now - startMs) / 1000));
		}
	}
	if (fromStart == null) return reported;
	if (reported == null) return fromStart;
	return Math.max(reported, fromStart);
}

export function localPreloadStatus (health, preload, nowMs) {
	if (!preload || preload.phase === 'skipped' || preload.alreadyLoaded) return '';
	const model = preload.model || (health && (health.narrativeModel || health.model)) || 'model';
	if (preload.phase === 'loading' || preload.state === 'loading') {
		const elapsed = preloadElapsedSeconds(preload, nowMs);
		const modelReady = health && (health.loadState === 'loaded' || health.narrativeModelLoaded === true);
		if (modelReady && (elapsed == null || elapsed === 0)) return '';
		return elapsed == null ? `Loading ${model}…` : `Loading ${model}… (${elapsed}s)`;
	}
	if (preload.phase === 'error' || preload.state === 'error') return preload.error || 'Model load failed';
	if (preload.phase === 'done' && preload.loadSeconds != null && preload.loadSeconds !== '') {
		const seconds = Number(preload.loadSeconds);
		const shown = Number.isFinite(seconds) ? seconds.toFixed(1) : preload.loadSeconds;
		return `Ready (${model}) — loaded in ${shown}s`;
	}
	return '';
}

function queryDebugLine (query) {
	const label = `${query.key}@${query.version}`
	const sql = query.renderedSql || query.renderError || ''
	if (query.status === 'error') {
		const message = query.error || query.renderError || 'query failed'
		return sql ? `${label} ERROR: ${message}\n${sql}` : `${label} ERROR: ${message}`
	}
	const rows = query.rowCount == null ? 'n/a' : query.rowCount
	return `${label} rows=${rows} post=${query.postProcess || ''}\n${sql}`
}

export function narrativeDebugText (debugInfo) {
	if (!debugInfo) return '';
	const usage = debugInfo.usage || {};
	const timing = debugInfo.timing || {};
	const estimated = debugInfo.usageEstimated ? ' (estimated from length)' : '';
	const completion = usage.completionTokens != null ? `${usage.completionTokens}${estimated}` : '';
	const lines = [];
	if (debugInfo.error) lines.push(`error: ${debugInfo.error}`);
	lines.push(
		`provider: ${debugInfo.provider || ''}`,
		`model: ${debugInfo.model || ''}`,
		'input: digest',
		`promptChars: ${debugInfo.promptChars != null ? debugInfo.promptChars : ''}`,
		`sampleLimit: ${debugInfo.sampleLimit != null ? debugInfo.sampleLimit : 'full'}`,
		`maxTokens: ${debugInfo.maxTokens != null ? debugInfo.maxTokens : ''}`,
		`timeoutSeconds: ${debugInfo.timeoutSeconds != null ? debugInfo.timeoutSeconds : ''}`,
		`jobElapsedMs: ${debugInfo.elapsedMs != null ? debugInfo.elapsedMs : ''}`,
		`loadMs: ${debugInfo.loadMs != null ? debugInfo.loadMs : ''}`,
		`loadConfig: ${debugInfo.loadConfig ? JSON.stringify(debugInfo.loadConfig) : ''}`,
		`codeWarning: ${debugInfo.codeWarning || ''}`,
		`firstTokenMs: ${timing.firstTokenMs != null ? timing.firstTokenMs : ''}`,
		`generationMs: ${timing.generationMs != null ? timing.generationMs : ''}`,
		`promptTokens: ${usage.promptTokens != null ? usage.promptTokens : ''}`,
		`completionTokens: ${completion}`,
		`tokensPerSecond: ${timing.tokensPerSecond != null ? timing.tokensPerSecond : ''}`,
		`finish: ${debugInfo.finishReason || ''}`,
		`streamed: ${debugInfo.streamed ? 'yes' : 'no'}`,
		`runId: ${debugInfo.runId != null ? debugInfo.runId : ''}`,
		'',
		'QUERIES',
		...(Array.isArray(debugInfo.queries) ? debugInfo.queries.map(queryDebugLine) : []),
		'',
		'DIGEST',
		debugInfo.digestText || '',
		'',
		'USER PROMPT',
		debugInfo.userPrompt || ''
	);
	return lines.join('\n');
}
